// US-HISTORY-05 -- guardian audit log view. Append-only -- no edit
// or delete affordance. Filterable by event_code, actor, date range.

import { type AuditLogEntry, auditLogList } from '@chorepop/core/client';
import { type Component, For, Match, Show, Switch, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  onBack: () => void;
};

export const AuditLogScreen: Component<Props> = (props) => {
  const [eventCode, setEventCode] = createSignal<string>('');
  const [atFromUtc, setAtFromUtc] = createSignal<string>('');
  const [atToUtc, setAtToUtc] = createSignal<string>('');

  const filterSource = () => [eventCode(), atFromUtc(), atToUtc()] as const;
  const [entries] = createResource(filterSource, async ([code, from, to]) => {
    const result = await auditLogList({
      sessionId: props.sessionId,
      ...(code !== '' ? { eventCode: code } : {}),
      ...(from !== '' ? { atFromUtc: `${from}T00:00:00.000Z` } : {}),
      ...(to !== '' ? { atToUtc: `${to}T23:59:59.999Z` } : {}),
    });
    if (!result.ok) throw new Error(result.error.message);
    return result.value;
  });

  const formatAt = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  /** Distinct event codes present in the current page so the filter
   *  dropdown only offers codes that have been logged. */
  const knownCodes = () => {
    const e = entries();
    if (!e) return [] as { code: string; label: string }[];
    const labels = new Map<string, string>();
    for (const row of e) labels.set(row.eventCode, row.eventLabel);
    return Array.from(labels, ([code, label]) => ({ code, label })).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  };

  const clearFilters = () => {
    setEventCode('');
    setAtFromUtc('');
    setAtToUtc('');
  };
  const anyActive = () => eventCode() !== '' || atFromUtc() !== '' || atToUtc() !== '';

  return (
    <ActionScreen
      class="action-screen--history"
      eyebrow="Family record"
      title="Activity history"
      onBack={props.onBack}
    >
      <section
        class="history-filter-card"
        style="display: flex; flex-wrap: wrap; gap: 8px; align-items: end;"
      >
        <label style="display: flex; flex-direction: column; gap: 4px;">
          <span class="field-label" style="font-size: 12px;">
            Event
          </span>
          <select value={eventCode()} onChange={(e) => setEventCode(e.currentTarget.value)}>
            <option value="">All events</option>
            <For each={knownCodes()}>
              {(event) => <option value={event.code}>{event.label}</option>}
            </For>
          </select>
        </label>
        <label style="display: flex; flex-direction: column; gap: 4px;">
          <span class="field-label" style="font-size: 12px;">
            From
          </span>
          <input
            type="date"
            value={atFromUtc()}
            onInput={(e) => setAtFromUtc(e.currentTarget.value)}
          />
        </label>
        <label style="display: flex; flex-direction: column; gap: 4px;">
          <span class="field-label" style="font-size: 12px;">
            To
          </span>
          <input type="date" value={atToUtc()} onInput={(e) => setAtToUtc(e.currentTarget.value)} />
        </label>
        <Show when={anyActive()}>
          <button type="button" class="ghost" onClick={clearFilters}>
            Clear filters
          </button>
        </Show>
      </section>

      <Switch>
        <Match when={entries.loading}>
          <section class="history-empty-card">
            <p>Loading audit log…</p>
          </section>
        </Match>
        <Match when={entries.error}>
          <section class="history-empty-card">
            <p class="form-error" role="alert">
              Couldn't load the audit log: {(entries.error as Error).message}
            </p>
          </section>
        </Match>
        <Match when={(entries()?.length ?? 0) === 0}>
          <section class="history-empty-card">
            <p>No audit events match the current filters.</p>
          </section>
        </Match>
        <Match when={(entries()?.length ?? 0) > 0}>
          <ul class="history-list">
            <For each={entries()}>{(row) => <AuditRow row={row} formatAt={formatAt} />}</For>
          </ul>
        </Match>
      </Switch>
    </ActionScreen>
  );
};

const AuditRow: Component<{
  row: AuditLogEntry;
  formatAt: (iso: string) => string;
}> = (props) => (
  <li class="history-row history-row--deposit" data-testid={`audit-row-${props.row.auditEventId}`}>
    <div class="history-row-leading">
      <span class="history-chip">{props.row.eventCategory}</span>
    </div>
    <div class="history-row-body">
      <div class="history-row-title">{props.row.eventLabel}</div>
      <div class="history-row-allocations" style="color: var(--ink-2);">
        {props.formatAt(props.row.eventAtUtc)}
        <Show when={props.row.actorDisplayName}>{(name) => ` · by ${name()}`}</Show>
        <Show
          when={
            props.row.subjectDisplayName && props.row.subjectAccountId !== props.row.actorAccountId
          }
        >
          {' · for '}
          {props.row.subjectDisplayName}
        </Show>
      </div>
    </div>
  </li>
);
